import { CalendarLayoutPayloadDTO } from '@api/ringil-api.v2';
import {
  CalendarLayoutConfig,
  CALENDAR_LAYOUT_VERSION,
  CalendarSurface,
  FragmentConfig,
  isCalendarFragmentKey,
  LayoutZone,
  SurfaceLayout,
  zonesForSurface,
} from './model';

/**
 * Translation between the backend payload (`CalendarLayoutPayloadDTO`) and the config the card and
 * the designer work with, plus the sanitising that makes a stored payload safe to render.
 *
 * The generated DTO types `card` and `detail` as `any` — the OpenAPI schema describes them as free
 * maps of zone → placements — so this module is the one place that pins them down.
 */

const sanitizeZone = (raw: unknown): FragmentConfig[] => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const items: FragmentConfig[] = [];
  raw.forEach(entry => {
    // Tolerate both the object form and a bare key string, so a hand-edited or imported config
    // does not have to be perfectly shaped to load.
    const key = typeof entry === 'string' ? entry : (entry as FragmentConfig)?.key;
    // Unknown keys are dropped rather than thrown on: a payload written by a newer build (or by a
    // backend that knows fragments this client does not) must still render.
    if (!isCalendarFragmentKey(key) || seen.has(key)) return;
    seen.add(key);
    // Only a real boolean is an answer. Anything else — an absent field, an explicit `null` from the
    // API, a hand-edited import — means "not configured", and must stay absent rather than being
    // coerced to false: writing `showIcon: false` back would silently switch the icon off for good.
    const showIcon = typeof entry === 'object' && entry !== null ? (entry as FragmentConfig).showIcon : undefined;
    items.push(typeof showIcon === 'boolean' ? { key, showIcon } : { key });
  });
  return items;
};

export const sanitizeSurface = (raw: unknown, surface: CalendarSurface): SurfaceLayout => {
  const layout: SurfaceLayout = {};
  const source = (raw ?? {}) as Record<string, unknown>;
  // Only zones the surface actually renders survive, so a fragment can never be stranded in a zone
  // that has no container to draw it in.
  zonesForSurface(surface).forEach(zone => {
    layout[zone] = sanitizeZone(source[zone]);
  });
  // A fragment placed in two zones would render twice; first placement wins.
  const claimed = new Set<string>();
  zonesForSurface(surface).forEach(zone => {
    layout[zone] = (layout[zone] ?? []).filter(item => {
      if (claimed.has(item.key)) return false;
      claimed.add(item.key);
      return true;
    });
  });
  return layout;
};

/**
 * Normalises a payload — from the API, from localStorage of an older build, or pasted into the
 * designer's import box — into a config this build can render.
 *
 * Returns null when the payload is not a usable config at all, so the caller falls back rather than
 * rendering an empty card. Future schema changes get their own branch here, keyed on `version`.
 */
export const migrateCalendarLayout = (raw: unknown): CalendarLayoutConfig | null => {
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as Partial<CalendarLayoutConfig>;
  if (!candidate.card && !candidate.detail) return null;
  return {
    version: CALENDAR_LAYOUT_VERSION,
    updatedAt: typeof candidate.updatedAt === 'string' ? candidate.updatedAt : new Date().toISOString(),
    card: sanitizeSurface(candidate.card, 'card'),
    detail: sanitizeSurface(candidate.detail, 'detail'),
  };
};

export const configFromPayload = (payload: CalendarLayoutPayloadDTO | undefined | null): CalendarLayoutConfig | null =>
  payload ? migrateCalendarLayout(payload) : null;

/**
 * The payload sent back to the backend. `updatedAt` is deliberately not included: the server owns
 * that timestamp and reports it as `publishedAt` / `draftSavedAt`.
 */
export const payloadFromConfig = (config: CalendarLayoutConfig): CalendarLayoutPayloadDTO => ({
  version: CALENDAR_LAYOUT_VERSION,
  card: sanitizeSurface(config.card, 'card'),
  detail: sanitizeSurface(config.detail, 'detail'),
});

/** Placement count across both surfaces — the designer shows it, and an empty model is worth flagging. */
export const fragmentCount = (config: CalendarLayoutConfig): number =>
  (['card', 'detail'] as CalendarSurface[]).reduce(
    (total, surface) =>
      total + zonesForSurface(surface).reduce((zoneTotal, zone) => zoneTotal + ((surface === 'card' ? config.card : config.detail)[zone]?.length ?? 0), 0),
    0,
  );

export const isZoneEmpty = (layout: SurfaceLayout, zone: LayoutZone): boolean => (layout[zone] ?? []).length === 0;
